import * as React from "react";
import {observer} from "mobx-react";
import {withAppStores} from "../../../../AppStores";
import type {IBasicPropsFor3DTweaking} from "../SpaceView";
import {ConfirmWindow} from "../../abstract/popups/ConfirmWindow";
import {isSpaceNamePresentInAnotherSpaceVersion, SpaceEditorMode} from "../spaceeditor/logic3d/renderers/SpaceViewRendererUtils";
import type {SpaceTool} from "../spaceeditor/logic3d/features/tools/Tools";
import {SpaceEditor} from "../spaceeditor/ui/SpaceEditor";
import {FileUtils} from "../../../../utils/file/FileUtils";
import {XyiconFeature} from "../../../../generated/api/base";
import {Space} from "../../../../data/models/Space";
import type {SpaceFileInsertionInfo} from "../../../../generated/api/base";
import type {SpaceVersion} from "../../../../data/models/SpaceVersion";
import {Functions} from "../../../../utils/function/Functions";
import type {AppState} from "../../../../data/state/AppState";
import type {App} from "../../../../App";
import type {Type} from "../../../../data/models/Type";
import {LargePDFWarning} from "../../../5.0/modules/space/LargePDFWarning";
import {Constants} from "../spaceeditor/logic3d/Constants";
import {TimeUtils} from "../../../../utils/TimeUtils";
import {PDFList} from "./PDFList";
import type {IOnePagePDFDoc} from "./CreateSpacePanel";

interface ISpaceConfirmSettingsProps extends IBasicPropsFor3DTweaking {
	readonly selectedVersion: SpaceVersion;
	readonly spaceType: Type;
	readonly pdfFiles: {
		originalFiles: File[];
		onePagePDFDocs: IOnePagePDFDoc[][]; // OriginalPDFFiles split by pages. pdfFiles.onePagePDFDocs[3] is an array of PDF documents that is generated by the pages of pdpfFiles.originalFiles[3]
	};
	readonly applySpaceUnitsPerMeterToAllSpaces: (spaceUnitsPerMeter: number) => void;
	readonly forceUpdate: () => void; // needed to update the "next" button (enabled/disabled)
	readonly appState?: AppState;
	readonly app?: App;
}

interface ISpaceConfirmSettingsState {
	activePDFFile: IOnePagePDFDoc;
	activeToolId: SpaceTool;
	isPDFLoaded: boolean;
	largePDFObject: {
		showTooLargePDFWarning: boolean;
		renderLargePDF: boolean;
		space: Space | null;
	};
}

@withAppStores("app", "appState")
@observer
export class SpaceConfirmSettings extends React.Component<ISpaceConfirmSettingsProps, ISpaceConfirmSettingsState> {
	constructor(props: ISpaceConfirmSettingsProps) {
		super(props);
		this.state = {
			activePDFFile: this.props.pdfFiles.onePagePDFDocs[0][0],
			activeToolId: "pan",
			isPDFLoaded: false,
			largePDFObject: {
				renderLargePDF: false,
				showTooLargePDFWarning: false,
				space: null,
			},
		};
	}

	private async loadSpace(space: Space) {
		await TimeUtils.waitForNextFrame();
		await TimeUtils.waitForNextFrame();
		await this.props.app.spaceViewRenderer.populateData(space);
	}

	private getFileContentOfPDF(pdf: IOnePagePDFDoc) {
		return pdf.doc.save();
	}

	private getFileSizeOfBytesInMB(bytes: Uint8Array) {
		return bytes.length / 1024 / 1024;
	}

	private async loadActivePDF(newActivePDF: IOnePagePDFDoc = this.state.activePDFFile) {
		let space: Space;

		const mode = this.getMode(newActivePDF);

		if (mode === SpaceEditorMode.ALIGN) {
			const spaceName = newActivePDF.spaceName;
			const spaces = this.props.appState.actions.getList<Space>(XyiconFeature.Space);

			space = spaces.find((space: Space) => space.name === spaceName);
		}

		const pdfContent = await this.getFileContentOfPDF(newActivePDF);

		const spaceFileData = {
			spaceFileID: null as string,
			sourceFile: this.getActivePDFURL(pdfContent),
			thumbnailFile: newActivePDF.thumbnail,
			settings: newActivePDF.insertionInfo ? {insertionInfo: newActivePDF.insertionInfo} : (null as {insertionInfo: SpaceFileInsertionInfo}),
			spaceVersionID: this.props.selectedVersion.id,
		};

		if (!space) {
			space = new Space(
				{
					spaceID: null,
					spaceRefID: null,
					spaceTypeID: this.props.spaceType.id,
					portfolioID: this.props.appState.activePortfolioId,
					name: newActivePDF.spaceName,
					unitsPerMeter: null,
					fieldData: null,
					settings: null,
					spaceFiles: [spaceFileData],
				},
				this.props.appState,
			);
		} else {
			space.addTemporarySpaceFile(spaceFileData);
		}

		if (newActivePDF.insertionInfo) {
			space.newestSpaceFile.setInsertionInfo(newActivePDF.insertionInfo);
		}

		const sizeInMB = this.getFileSizeOfBytesInMB(pdfContent);

		this.setState({
			isPDFLoaded: true,
		});

		if (sizeInMB >= Constants.SIZE.PDF_WARNING_THRESHOLD) {
			this.setState({
				largePDFObject: {
					showTooLargePDFWarning: true,
					renderLargePDF: false,
					space,
				},
			});
		} else {
			await this.loadSpace(space);
		}
	}

	private onSelect = async (originalPDFIndex: number, originalPageNumber: number) => {
		const setNewState = async () => {
			this.setActiveTool("pan");
			const newActivePDF = this.props.pdfFiles.onePagePDFDocs[originalPDFIndex][originalPageNumber];

			this.setState({
				isPDFLoaded: false,
				activePDFFile: newActivePDF,
				largePDFObject: {
					renderLargePDF: false,
					showTooLargePDFWarning: false,
					space: null,
				},
			});
			await this.loadActivePDF(newActivePDF);
		};

		if (!this.state.largePDFObject.showTooLargePDFWarning && this.state.activePDFFile.spaceId && !this.state.activePDFFile.isAlignmentConfirmed) {
			const confirmed = await ConfirmWindow.open("Are you sure you want to switch without confirming your alignment first?");

			if (confirmed) {
				await setNewState();
			}
		} else {
			await setNewState();
		}
	};

	private getActivePDFURL(pdfData: Uint8Array) {
		return FileUtils.createURLFromData(pdfData as BlobPart);
	}

	private setScale = (spaceUnitsPerMeter: number, applyToAllSpaces: boolean = false) => {
		if (applyToAllSpaces) {
			this.props.applySpaceUnitsPerMeterToAllSpaces(spaceUnitsPerMeter);
		} else {
			// eslint-disable-next-line @eslint-react/no-direct-mutation-state
			this.state.activePDFFile.spaceUnitsPerMeter = spaceUnitsPerMeter;
			this.props.forceUpdate();
			this.forceUpdate();
		}
	};

	private confirmAlignment = (insertionInfo: SpaceFileInsertionInfo) => {
		const activePDFFile = this.state.activePDFFile;

		activePDFFile.insertionInfo = insertionInfo;
		activePDFFile.isAlignmentConfirmed = true;

		const space = this.props.appState.actions.getFeatureItemById(activePDFFile.spaceId, XyiconFeature.Space) as Space;

		space.newestSpaceFile.setInsertionInfo(insertionInfo);

		this.props.forceUpdate();
		this.forceUpdate();
	};

	private setActiveTool = (id: SpaceTool) => {
		this.setState({
			activeToolId: id,
		});
	};

	private getMode(activePDFFile: IOnePagePDFDoc = this.state.activePDFFile) {
		const spaceName = activePDFFile.spaceName;

		return isSpaceNamePresentInAnotherSpaceVersion(this.props.appState.actions, spaceName) ? SpaceEditorMode.ALIGN : SpaceEditorMode.SET_SCALE;
	}

	private onRenderPDFClick = async () => {
		this.setState((prevState) => {
			const {activePDFFile} = prevState;
			activePDFFile.isSkipped = false;
			const mode = this.getMode(activePDFFile);

			if (mode === SpaceEditorMode.ALIGN) {
				activePDFFile.isAlignmentConfirmed = false;
			} else {
				prevState.activePDFFile.spaceUnitsPerMeter = null;
			}
			this.props.forceUpdate();

			return {
				largePDFObject: {
					showTooLargePDFWarning: false,
					renderLargePDF: true,
					space: prevState.largePDFObject.space,
				},
			};
		});
		await this.loadSpace(this.state.largePDFObject.space);
	};

	private onSkipClick = () => {
		const {activePDFFile, largePDFObject} = this.state;
		activePDFFile.isSkipped = true;
		const mode = this.getMode(activePDFFile);

		if (mode === SpaceEditorMode.ALIGN) {
			this.confirmAlignment(largePDFObject.space.newestSpaceFile.insertionInfo);
		} else {
			activePDFFile.spaceUnitsPerMeter = 1; // just a default value, usually too large, so it's quite obvious that it's "wrong"
			this.props.forceUpdate();
			this.forceUpdate();
		}
	};

	public override async componentDidMount() {
		await this.loadActivePDF();
	}

	public override render() {
		const {activePDFFile, largePDFObject} = this.state;
		const mode = this.getMode(activePDFFile);
		const replacementText = mode === SpaceEditorMode.ALIGN ? "alignment" : "scale";

		return (
			<div className="SpaceScaleSettings hbox">
				<PDFList
					selectedVersion={this.props.selectedVersion}
					pdfFiles={this.props.pdfFiles}
					mode="SetScaleOrAlign"
					activePDFFile={activePDFFile}
					onSelect={this.onSelect}
					forceUpdate={this.props.forceUpdate}
				/>
				{largePDFObject.showTooLargePDFWarning && !largePDFObject.renderLargePDF ? (
					<LargePDFWarning
						replacementText={replacementText}
						onRenderPDFClick={this.onRenderPDFClick}
						onSkipClick={this.onSkipClick}
						isSkipped={activePDFFile.isSkipped}
					/>
				) : (
					this.state.isPDFLoaded && (
						<SpaceEditor
							mode={this.getMode()}
							setScale={this.setScale}
							confirmAlignment={this.confirmAlignment}
							setActiveTool={this.setActiveTool}
							activeToolId={this.state.activeToolId}
							selectionToolType="selection"
							onAddCatalogClick={Functions.emptyFunction}
							onDuplicateCatalogClick={Functions.emptyFunction}
							spaceViewShareClick={Functions.emptyFunction}
							onOpenSpaceToPDFExportPanel={Functions.emptyFunction}
						/>
					)
				)}
			</div>
		);
	}
}
